import { LIMONADE_MOTIONLINK_SCHEMA, } from './types.js';
const MOTION_TYPES = new Set([
    'static', 'handheld', 'pan_left', 'pan_right', 'tilt_up', 'tilt_down', 'roll',
    'push_in', 'pull_out', 'orbit_left', 'orbit_right', 'crane_up', 'crane_down',
    'shake', 'unknown',
]);
const PHONE_MOUNT_ORIENTATIONS = new Set([
    'top', 'side_left', 'side_right', 'upside_down', 'custom',
]);
const isRecord = (value) => typeof value === 'object' && value !== null && !Array.isArray(value);
const isNumber = (value) => typeof value === 'number' && Number.isFinite(value);
const isNullableString = (value) => value === null || typeof value === 'string';
const isVector3D = (value) => value === null || (isRecord(value) && isNumber(value.x) && isNumber(value.y) && isNumber(value.z));
const isQuaternion = (value) => value === null || (isRecord(value) && isNumber(value.x) && isNumber(value.y) && isNumber(value.z) && isNumber(value.w));
const isEuler = (value) => value === null || (isRecord(value) && isNumber(value.pitch) && isNumber(value.yaw) && isNumber(value.roll));
const optionalNumber = (value, fallback = 0) => typeof value === 'number' && Number.isFinite(value) ? value : fallback;
const optionalString = (value) => typeof value === 'string' ? value : null;
function vector(value) {
    if (!isRecord(value))
        return null;
    return {
        x: optionalNumber(value.x),
        y: optionalNumber(value.y),
        z: optionalNumber(value.z),
    };
}
function quaternion(value) {
    if (!isRecord(value))
        return null;
    return {
        x: optionalNumber(value.x),
        y: optionalNumber(value.y),
        z: optionalNumber(value.z),
        w: optionalNumber(value.w, 1),
    };
}
function euler(value) {
    if (!isRecord(value))
        return null;
    return {
        pitch: optionalNumber(value.pitch),
        yaw: optionalNumber(value.yaw),
        roll: optionalNumber(value.roll),
        order: 'XYZ',
    };
}
function calibrationQuality(value) {
    if (isNumber(value))
        return Math.max(0, Math.min(1, value));
    switch (value) {
        case 'good': return 0.9;
        case 'fair': return 0.65;
        case 'poor': return 0.3;
        case 'unknown':
        default: return 0.2;
    }
}
function normalizeTrackingState(value) {
    if (value === 'normal')
        return 'normal';
    if (typeof value === 'string' && value.startsWith('limited'))
        return 'limited';
    if (value === 'not_available')
        return 'not_available';
    return 'unknown';
}
function mountRotationOffset(orientation) {
    const halfTurn = Math.PI / 2;
    const sinHalf = Math.sin(halfTurn / 2);
    const cosHalf = Math.cos(halfTurn / 2);
    switch (orientation) {
        case 'side_left':
            return { x: 0, y: 0, z: sinHalf, w: cosHalf };
        case 'side_right':
            return { x: 0, y: 0, z: -sinHalf, w: cosHalf };
        case 'upside_down':
            return { x: 0, y: 0, z: 1, w: 0 };
        case 'top':
        case 'custom':
        default:
            return { x: 0, y: 0, z: 0, w: 1 };
    }
}
function isCaptureAppSession(value) {
    return (isRecord(value) &&
        value.schema === LIMONADE_MOTIONLINK_SCHEMA &&
        typeof value.session_id === 'string' &&
        Array.isArray(value.samples) &&
        value.samples.some((sample) => isRecord(sample) && (isRecord(sample.imu) || isRecord(sample.arkit))));
}
function normalizeCaptureCalibration(value, createdAt) {
    const calibration = isRecord(value) ? value : {};
    const rotationOffset = quaternion(calibration.rotation_offset);
    const positionOffset = vector(calibration.position_offset);
    const lensOffset = vector(calibration.lens_offset);
    const orientation = PHONE_MOUNT_ORIENTATIONS.has(String(calibration.phone_mount_orientation))
        ? String(calibration.phone_mount_orientation)
        : 'top';
    const quality = calibrationQuality(calibration.calibration_quality);
    const mountRotation = mountRotationOffset(orientation);
    return {
        calibrated_at: quality > 0.25 ? createdAt : null,
        camera_name: optionalString(calibration.camera_name),
        lens_name: optionalString(calibration.lens_name),
        phone_mount_orientation: orientation,
        phone_mount_position: orientation === 'top' ? 'top' : 'unknown',
        phone_to_camera_rotation: mountRotation,
        phone_to_camera_translation_m: positionOffset && lensOffset ? {
            x: positionOffset.x + lensOffset.x,
            y: positionOffset.y + lensOffset.y,
            z: positionOffset.z + lensOffset.z,
        } : positionOffset,
        baseline_orientation: rotationOffset,
        rotation_offset: mountRotation,
        position_offset: positionOffset,
        lens_offset: lensOffset,
        scale_factor: isNumber(calibration.scale_factor) ? calibration.scale_factor : 1,
        stabilization_strength: isNumber(calibration.stabilization_strength) ? calibration.stabilization_strength : 0.35,
        calibration_quality: quality,
        sensor_time_offset_ms: isNumber(calibration.sensor_time_offset_ms) ? calibration.sensor_time_offset_ms : 0,
        notes: typeof calibration.notes === 'string' ? calibration.notes : undefined,
    };
}
function normalizeCaptureSample(sample, index) {
    if (!isRecord(sample) || !isNumber(sample.timestamp_ms))
        return null;
    const timestampMs = sample.timestamp_ms;
    const frameNumber = Number.isInteger(sample.frame_number) ? Number(sample.frame_number) : index + 1;
    const imuRecord = isRecord(sample.imu) ? sample.imu : null;
    const arkitRecord = isRecord(sample.arkit) ? sample.arkit : null;
    const imuQuaternion = imuRecord ? quaternion(imuRecord.attitude_quaternion) : null;
    const imuEuler = imuRecord ? euler(imuRecord.euler) : null;
    const imuAcceleration = imuRecord ? vector(imuRecord.acceleration) : null;
    const imuAngularVelocity = imuRecord ? vector(imuRecord.rotation_rate) : null;
    const arkitQuaternion = arkitRecord ? quaternion(arkitRecord.orientation_quaternion) : null;
    const arkitPosition = arkitRecord ? vector(arkitRecord.position) : null;
    const arkitConfidence = arkitRecord ? optionalNumber(arkitRecord.tracking_confidence, 0.45) : null;
    const transform = arkitRecord && Array.isArray(arkitRecord.transform)
        ? arkitRecord.transform.filter(isNumber)
        : undefined;
    const imu = imuRecord ? {
        sample_type: 'imu',
        timestamp_ms: timestampMs,
        frame_number: frameNumber,
        rotation_quaternion: imuQuaternion,
        rotation_euler: imuEuler,
        acceleration: imuAcceleration,
        angular_velocity: imuAngularVelocity,
        position: null,
        velocity: null,
        tracking_confidence: 0.75,
        motion_type: 'unknown',
        gravity: null,
    } : undefined;
    const arkit = arkitRecord ? {
        sample_type: 'arkit_pose',
        timestamp_ms: timestampMs,
        frame_number: frameNumber,
        rotation_quaternion: arkitQuaternion,
        rotation_euler: null,
        acceleration: null,
        angular_velocity: null,
        position: arkitPosition,
        velocity: null,
        tracking_confidence: arkitConfidence ?? 0.45,
        motion_type: 'unknown',
        transform_matrix: transform,
        tracking_state: normalizeTrackingState(arkitRecord.tracking_state),
    } : undefined;
    const rotationQuaternion = arkit?.rotation_quaternion ?? imu?.rotation_quaternion ?? null;
    const rotationEuler = imu?.rotation_euler ?? null;
    const trackingConfidence = arkit?.tracking_confidence ?? imu?.tracking_confidence ?? 0.5;
    return {
        sample_type: 'frame',
        timestamp_ms: timestampMs,
        frame_number: frameNumber,
        rotation_quaternion: rotationQuaternion,
        rotation_euler: rotationEuler,
        acceleration: imu?.acceleration ?? null,
        angular_velocity: imu?.angular_velocity ?? null,
        position: arkit?.position ?? null,
        velocity: null,
        tracking_confidence: trackingConfidence,
        motion_type: 'unknown',
        ...(imu ? { imu } : {}),
        ...(arkit ? { arkit_pose: arkit } : {}),
    };
}
export function normalizeMotionLinkCaptureSession(input) {
    if (!isCaptureAppSession(input))
        return null;
    const createdAt = typeof input.created_at === 'string' ? input.created_at : new Date().toISOString();
    const rawSamples = Array.isArray(input.samples) ? input.samples : [];
    const samples = rawSamples
        .map((sample, index) => normalizeCaptureSample(sample, index))
        .filter((sample) => sample !== null)
        .sort((a, b) => a.timestamp_ms - b.timestamp_ms);
    const imuCount = samples.filter((sample) => sample.imu).length;
    const arkitCount = samples.filter((sample) => sample.arkit_pose).length;
    const durationMs = isRecord(input.summary) && isNumber(input.summary.duration_ms)
        ? input.summary.duration_ms
        : samples.length
            ? samples[samples.length - 1].timestamp_ms - samples[0].timestamp_ms
            : null;
    const device = isRecord(input.device) ? input.device : {};
    return {
        schema: LIMONADE_MOTIONLINK_SCHEMA,
        session_id: String(input.session_id),
        created_at: createdAt,
        device: {
            manufacturer: 'Apple',
            model: optionalString(device.model),
            os: optionalString(device.os),
            app_version: optionalString(device.app_version),
        },
        rig_calibration: normalizeCaptureCalibration(input.rig_calibration, createdAt),
        samples,
        summary: {
            sample_count: samples.length,
            duration_ms: durationMs,
            detected_motion_types: ['unknown'],
            dominant_motion_type: 'unknown',
            confidence: {
                overall_confidence: samples.length ? 0.65 : 0,
                imu_confidence: imuCount ? 0.75 : 0,
                arkit_confidence: arkitCount ? 0.75 : 0,
                visual_confidence: 0,
                fusion_confidence: imuCount && arkitCount ? 0.7 : 0.45,
                warnings: [
                    ...(input.rig_calibration && isRecord(input.rig_calibration) && input.rig_calibration.calibration_quality !== 'unknown'
                        ? []
                        : ['Calibration missing']),
                ],
            },
        },
    };
}
function isMotionSample(value) {
    if (!isRecord(value))
        return false;
    return (['frame', 'imu', 'arkit_pose', 'camera_motion_estimate'].includes(String(value.sample_type)) &&
        isNumber(value.timestamp_ms) &&
        (value.frame_number === undefined || Number.isInteger(value.frame_number)) &&
        isQuaternion(value.rotation_quaternion) &&
        isEuler(value.rotation_euler) &&
        isVector3D(value.acceleration) &&
        isVector3D(value.angular_velocity) &&
        isVector3D(value.position) &&
        isVector3D(value.velocity) &&
        isNumber(value.tracking_confidence) && value.tracking_confidence >= 0 && value.tracking_confidence <= 1 &&
        MOTION_TYPES.has(String(value.motion_type)));
}
export function isMotionLinkSession(value) {
    if (!isRecord(value) || !isRecord(value.device) || !isRecord(value.rig_calibration) || !isRecord(value.summary)) {
        return false;
    }
    const confidence = value.summary.confidence;
    return (value.schema === LIMONADE_MOTIONLINK_SCHEMA &&
        typeof value.session_id === 'string' && value.session_id.length > 0 &&
        typeof value.created_at === 'string' &&
        isNullableString(value.device.manufacturer) &&
        isNullableString(value.device.model) &&
        isNullableString(value.device.os) &&
        isNullableString(value.device.app_version) &&
        isNullableString(value.rig_calibration.calibrated_at) &&
        isNullableString(value.rig_calibration.camera_name) &&
        isNullableString(value.rig_calibration.lens_name) &&
        PHONE_MOUNT_ORIENTATIONS.has(String(value.rig_calibration.phone_mount_orientation)) &&
        (value.rig_calibration.phone_mount_position === undefined || typeof value.rig_calibration.phone_mount_position === 'string') &&
        isQuaternion(value.rig_calibration.phone_to_camera_rotation) &&
        isVector3D(value.rig_calibration.phone_to_camera_translation_m) &&
        isQuaternion(value.rig_calibration.baseline_orientation) &&
        isQuaternion(value.rig_calibration.rotation_offset) &&
        isVector3D(value.rig_calibration.position_offset) &&
        isVector3D(value.rig_calibration.lens_offset) &&
        isNumber(value.rig_calibration.scale_factor) &&
        isNumber(value.rig_calibration.stabilization_strength) &&
        isNumber(value.rig_calibration.calibration_quality) &&
        isNumber(value.rig_calibration.sensor_time_offset_ms) &&
        Array.isArray(value.samples) && value.samples.every(isMotionSample) &&
        Number.isInteger(value.summary.sample_count) &&
        value.summary.sample_count === value.samples.length &&
        (value.summary.duration_ms === null || isNumber(value.summary.duration_ms)) &&
        Array.isArray(value.summary.detected_motion_types) &&
        value.summary.detected_motion_types.every((motionType) => MOTION_TYPES.has(String(motionType))) &&
        MOTION_TYPES.has(String(value.summary.dominant_motion_type)) &&
        isRecord(confidence) &&
        isNumber(confidence.overall_confidence) &&
        isNumber(confidence.imu_confidence) &&
        isNumber(confidence.arkit_confidence) &&
        isNumber(confidence.visual_confidence) &&
        isNumber(confidence.fusion_confidence) &&
        Array.isArray(confidence.warnings) &&
        confidence.warnings.every((warning) => typeof warning === 'string'));
}
//# sourceMappingURL=motionlink.js.map